import cx from "classnames";
import type { MouseEvent, ReactNode } from "react";
import { useCallback, useState } from "react";

import Animation from "metabase/css/core/animation.module.css";
import type { HoverCardProps } from "metabase/ui";
import { Box, HoverCard, useDelayGroup } from "metabase/ui";

// Initially, the user will have to hover for this long to open the popover
const POPOVER_SLOW_OPEN_DELAY = 250;

// When an item in the same delay group is already open, we want to open the
// popover immediately, without waiting for the user to hover for POPOVER_SLOW_OPEN_DELAY.
// This way the user can move the cursor between hover targets and get feedback immediately.
//
// When opening fast, we still delay a little bit to avoid a flickering popover
// when the target is being clicked.
const POPOVER_FAST_OPEN_DELAY = 150;

// When switching to another hover target in the same delay group,
// we don't close immediately but delay by a short amount to avoid flicker.
const POPOVER_CLOSE_DELAY = POPOVER_FAST_OPEN_DELAY + 30;

import S from "./Popover.module.css";

const DEFAULT_MIDDLEWARES: HoverCardProps["middlewares"] = {
  size: true,
  shift: true,
  flip: false,
};

export type PopoverProps = Pick<
  HoverCardProps,
  "children" | "position" | "disabled" | "radius" | "middlewares" | "offset"
> & {
  width?: number;
  content: ReactNode;
  openDelay?: number;
};

export function Popover({
  position = "bottom-start",
  disabled,
  radius,
  offset,
  middlewares = DEFAULT_MIDDLEWARES,
  content,
  openDelay = POPOVER_SLOW_OPEN_DELAY,
  width,
  children,
}: PopoverProps) {
  const group = useDelayGroup();

  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = useCallback(() => {
    group.onOpen();
    setIsOpen(true);
  }, [group]);

  const handleClose = useCallback(() => {
    group.onClose();
    setIsOpen(false);
  }, [group]);

  return (
    <HoverCard
      position={position}
      disabled={disabled}
      radius={radius}
      offset={offset}
      openDelay={group.shouldDelay ? openDelay : POPOVER_FAST_OPEN_DELAY}
      closeDelay={POPOVER_CLOSE_DELAY}
      onOpen={handleOpen}
      onClose={handleClose}
      middlewares={middlewares}
    >
      <HoverCard.Target>{children}</HoverCard.Target>
      <HoverCard.Dropdown
        onClick={stopPropagation}
        onMouseDown={stopPropagation}
        onMouseUp={stopPropagation}
        className={cx(S.dropdown, { [Animation.fadeIn]: group.shouldDelay })}
      >
        <Box fz="md" miw={width ?? 220} maw={width ?? 300}>
          {isOpen && content}
        </Box>
      </HoverCard.Dropdown>
    </HoverCard>
  );
}

function stopPropagation(evt: MouseEvent) {
  evt.stopPropagation();
}
